import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const SLIDE_DISTANCE = 32;

function getInitialTransform(config) {
  const effect = config?.entryEffect || {};
  if (effect.type === "rotate") {
    return "rotate(-12deg) scale(0.96)";
  }
  if (effect.type !== "slide") {
    return "";
  }
  const direction = effect.direction || "bottom";
  const distanceMap = {
    top: `translate3d(0, -${SLIDE_DISTANCE}px, 0)`,
    right: `translate3d(${SLIDE_DISTANCE}px, 0, 0)`,
    bottom: `translate3d(0, ${SLIDE_DISTANCE}px, 0)`,
    left: `translate3d(-${SLIDE_DISTANCE}px, 0, 0)`,
  };
  return distanceMap[direction] || distanceMap.bottom;
}

function isEntryEffectEnabled(config) {
  return !!config?.entryEffect?.enabled && !!config?.entryEffect?.type;
}

function getTargetElement(target) {
  if (!target) {
    return null;
  }
  return target.$el || target;
}

export function useEntryEffect(configSource) {
  const targetRef = ref(null);
  const hasEntered = ref(false);
  const effectActive = ref(false);
  let observer = null;
  let cleanupTimer = null;

  const currentConfig = computed(() => {
    return typeof configSource === "function" ? configSource() || {} : configSource || {};
  });

  const entryEffect = computed(() => currentConfig.value?.entryEffect || {});

  const resetObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const resetEffectTimer = () => {
    if (cleanupTimer) {
      clearTimeout(cleanupTimer);
      cleanupTimer = null;
    }
  };

  const scheduleEffectCleanup = () => {
    resetEffectTimer();
    const duration = entryEffect.value.duration || 600;
    const delay = entryEffect.value.delay || 0;
    cleanupTimer = setTimeout(() => {
      effectActive.value = false;
      cleanupTimer = null;
    }, delay + duration + 80);
  };

  const observeTarget = async () => {
    resetObserver();
    resetEffectTimer();
    hasEntered.value = false;
    effectActive.value = false;
    if (!isEntryEffectEnabled(currentConfig.value)) {
      hasEntered.value = true;
      return;
    }
    effectActive.value = true;
    await nextTick();
    const targetElement = getTargetElement(targetRef.value);
    if (!targetElement || typeof IntersectionObserver === "undefined") {
      hasEntered.value = true;
      effectActive.value = false;
      return;
    }
    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          hasEntered.value = true;
          scheduleEffectCleanup();
          resetObserver();
        }
      },
      {
        threshold: 0.1,
      }
    );
    observer.observe(targetElement);
  };

  const entryEffectStyle = computed(() => {
    if (!isEntryEffectEnabled(currentConfig.value)) {
      return {};
    }
    if (hasEntered.value && !effectActive.value) {
      return {};
    }
    const duration = entryEffect.value.duration || 600;
    const delay = entryEffect.value.delay || 0;
    const easing = entryEffect.value.easing || "ease-out";
    const style = {
      opacity: hasEntered.value ? 1 : 0,
      transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
      transitionDelay: `${delay}ms`,
    };
    if (!hasEntered.value) {
      const initialTransform = getInitialTransform(currentConfig.value);
      if (initialTransform) {
        style.transform = initialTransform;
      }
      style.willChange = "opacity, transform";
    }
    return style;
  });

  onMounted(() => {
    observeTarget();
  });

  watch(
    () => [
      currentConfig.value?.entryEffect?.enabled,
      currentConfig.value?.entryEffect?.type,
      currentConfig.value?.entryEffect?.direction,
      currentConfig.value?.entryEffect?.duration,
      currentConfig.value?.entryEffect?.delay,
      currentConfig.value?.entryEffect?.easing,
    ],
    () => {
      observeTarget();
    }
  );

  watch(targetRef, () => {
    observeTarget();
  });

  onBeforeUnmount(() => {
    resetObserver();
    resetEffectTimer();
  });

  return {
    targetRef,
    entryEffectStyle,
  };
}
